import { Box, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

import { getGuideMediaType, getVimeoEmbedUrl, getYouTubeEmbedUrl } from "../lib/guideMedia";

export interface GuideMediaProps {
  alt?: string;
  sx?: SxProps<Theme>;
  url: string;
}

export function GuideMedia(props: GuideMediaProps) {
  const { url, alt = "", sx } = props;
  const mediaType = getGuideMediaType(url);

  if (mediaType === "youtube" || mediaType === "vimeo") {
    const embedUrl = mediaType === "youtube" ? getYouTubeEmbedUrl(url) : getVimeoEmbedUrl(url);

    return (
      <Box
        allowFullScreen
        component="iframe"
        // `allow-same-origin` lets YouTube's player read its own cookies;
        // safe alongside `allow-scripts` because the iframe is cross-origin
        // from our app (the MDN sandbox-escape concern only applies when
        // iframe and host share an origin).
        sandbox="allow-scripts allow-presentation allow-popups allow-same-origin"
        src={embedUrl}
        title={alt || "Embedded video"}
        sx={mergeSxProps(
          {
            border: 0,
            aspectRatio: "16 / 9",
            maxHeight: 280,
            objectFit: "contain",
          },
          sx
        )}
      />
    );
  }

  if (mediaType === "video") {
    return (
      <Box
        controls
        autoPlay={false}
        component="video"
        src={url}
        sx={mergeSxProps(
          {
            maxHeight: 280,
            objectFit: "contain",
          },
          sx
        )}
      >
        <track kind="captions" />
      </Box>
    );
  }

  return (
    <Box
      alt={alt}
      component="img"
      src={url}
      sx={mergeSxProps(
        {
          maxHeight: 280,
          objectFit: "contain",
        },
        sx
      )}
    />
  );
}
